
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"

interface PaymentReceiptProps {
  formData: any
  selectedDeliveryOption?: string
  amount?: number
}

export function usePaymentReceipt({ formData, selectedDeliveryOption, amount }: PaymentReceiptProps) {
  const { toast } = useToast()
  const [emailSending, setEmailSending] = useState(false)
  const [emailResponse, setEmailResponse] = useState<any>(null)
  const [emailDebugInfo, setEmailDebugInfo] = useState<any>(null)
  const [paymentReceiptSent, setPaymentReceiptSent] = useState(false)

  const sendPaymentReceiptEmail = async () => {
    if (paymentReceiptSent) {
      console.log("Payment receipt already sent, skipping");
      return true;
    }
    
    setEmailSending(true);
    setEmailDebugInfo(null);
    setEmailResponse(null);
    
    try {
      console.log('Sending payment receipt email with data:', {
        customerEmail: formData.sender.email,
        customerName: `${formData.sender.firstName} ${formData.sender.lastName}`,
        deliveryMethod: selectedDeliveryOption,
        amount: amount,
      });
      
      // Prepare payload for the email service
      const payload = {
        type: 'payment_receipt',
        data: {
          customerEmail: formData.sender.email,
          customerName: `${formData.sender.firstName} ${formData.sender.lastName}`,
          deliveryMethod: selectedDeliveryOption,
          amount: amount,
        }
      };
      
      console.log('Payment receipt email payload:', JSON.stringify(payload, null, 2));
      setEmailDebugInfo(payload);
      
      // Call the new email service function
      const { error, data } = await supabase.functions.invoke('email-service', {
        body: payload
      });

      setEmailResponse(data || {});
      
      if (error) {
        console.error('Error sending payment receipt:', error);
        toast({
          variant: "warning",
          title: "Receipt Email Notice",
          description: `Payment successful, but there was an issue sending your receipt email. We'll still process your order.`,
        });
        return false;
      } else {
        console.log('Payment receipt email sent successfully', data);
        setPaymentReceiptSent(true);
        return true;
      }
    } catch (error: any) {
      console.error('Exception during payment receipt email sending:', error);
      setEmailResponse({ error: error.message || 'Unknown error' });
      toast({
        variant: "warning",
        title: "Receipt Email Notice",
        description: `Payment successful, but there was an issue sending your receipt email. We'll still process your order.`,
      });
      return false;
    } finally {
      setEmailSending(false);
    }
  }

  return {
    emailSending,
    emailResponse,
    emailDebugInfo,
    sendPaymentReceiptEmail
  }
}
